import React, { useEffect, useState } from "react";
import { EmployeeInfo, RoleInfo } from "../interfaces/EmployeeInfoInterface";
import { KeyRound, X, Check, AlertTriangle } from "lucide-react";

/**
 * Edit Employee Role — v2 modal (Employees page).
 * Rendered inside the page's `.emp` scope, so it reuses the shared
 * emp-modal / emp-to-* classes from EmployeeView.css.
 */
interface EmployeeRoleEditModalProps {
    isOpen: boolean;
    employee: EmployeeInfo;
    roles: RoleInfo[];
    onClose: () => void;
    onSave: (employeeID: number, roleID: number) => Promise<void>;
}

function initials(first: string, last: string): string {
    return `${(first || "?").charAt(0)}${(last || "").charAt(0)}`.toUpperCase();
}

const EmployeeRoleEditModal: React.FC<EmployeeRoleEditModalProps> = ({
    isOpen,
    employee,
    roles,
    onClose,
    onSave,
}) => {
    const [selectedRoleID, setSelectedRoleID] = useState<number>(employee.roleID);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        setSelectedRoleID(employee.roleID);
    }, [employee]);

    // Esc closes (matching the page's other v2 modals).
    useEffect(() => {
        if (!isOpen) return;
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !isSaving) onClose();
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [isOpen, isSaving, onClose]);

    if (!isOpen) return null;

    const currentRole = roles.find((r) => r.roleID === employee.roleID);
    const roleInList = roles.some((r) => r.roleID === selectedRoleID);
    const isUnchanged = selectedRoleID === employee.roleID;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(employee.employeeID, selectedRoleID);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="emp-modal-bg" onClick={() => !isSaving && onClose()}>
            <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="emp-modal-head">
                    <div className="mi">
                        <KeyRound size={21} />
                    </div>
                    <div>
                        <h3>Edit Employee Role</h3>
                        <div className="ms">
                            {employee.firstName} {employee.lastName} · {employee.altEmployeeID || "—"}
                        </div>
                    </div>
                    <button type="button" className="x" onClick={onClose} disabled={isSaving}>
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="emp-modal-body">
                    {/* Identity card */}
                    <div className="emp-confirm-emp">
                        <span className="av">{initials(employee.firstName, employee.lastName)}</span>
                        <div>
                            <div className="nm">
                                {employee.firstName} {employee.lastName}
                            </div>
                            <div className="who-sub emp-mono">
                                {employee.altEmployeeID || "—"} ·{" "}
                                {currentRole ? `${currentRole.name} (${currentRole.roleID})` : `Role ${employee.roleID}`}
                            </div>
                        </div>
                    </div>

                    {/* Role select */}
                    <div className="emp-to-frow">
                        <label>
                            Employee app role <span className="emp-req">*</span>
                        </label>
                        <select
                            className="emp-select"
                            value={selectedRoleID}
                            onChange={(e) => setSelectedRoleID(parseInt(e.target.value, 10))}
                            disabled={isSaving}
                        >
                            {/* Keep an inactive/unlisted current role selectable so the form is never blank */}
                            {!roleInList && (
                                <option value={selectedRoleID}>
                                    {currentRole ? `${currentRole.name} (${currentRole.roleID})` : `Role ${selectedRoleID}`}
                                </option>
                            )}
                            {roles.map((role) => (
                                <option key={role.roleID} value={role.roleID}>
                                    {role.name} ({role.roleID})
                                </option>
                            ))}
                        </select>
                        <div className="emp-to-help">
                            Controls what the employee can record in the mobile field app (hours, tasks, photos…).
                        </div>
                    </div>

                    {!roleInList && (
                        <div className="emp-to-warn-banner">
                            <AlertTriangle size={17} />
                            <div>
                                The employee&apos;s current role isn&apos;t in the active role list — choosing a new
                                role can&apos;t be undone from here.
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="emp-modal-foot">
                    <button type="button" className="emp-btn" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="emp-btn primary"
                        onClick={handleSave}
                        disabled={isSaving || isUnchanged}
                    >
                        {isSaving ? (
                            <>
                                <span className="emp-spin" /> Saving…
                            </>
                        ) : (
                            <>
                                <Check size={16} />
                                Save role
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmployeeRoleEditModal;
